import React from 'react';
import { Target, Clock, DollarSign } from 'lucide-react';

/**
 * Компонент для выбора цели оптимизации кредита
 */
const OptimizationSection = ({ 
  formData, 
  handleInputChange,
  errors 
}) => {
  return (
    <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 p-5 rounded-lg border border-blue-200 dark:border-blue-800/50 shadow-sm">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <Target className="w-5 h-5 text-blue-500" />
        Цель рефинансирования
      </h3>
      
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Что вы хотите оптимизировать?
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Вариант "Снизить ежемесячный платеж" */}
          <label className={`
            relative flex items-center p-4 rounded-lg border cursor-pointer transition-colors
            ${formData.optimizationGoal === 'payment' 
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-600' 
              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'}
          `}>
            <input
              type="radio"
              name="optimizationGoal"
              value="payment"
              checked={formData.optimizationGoal === 'payment'}
              onChange={handleInputChange}
              className="form-radio sr-only"
            />
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-full ${formData.optimizationGoal === 'payment' ? 'bg-blue-100 dark:bg-blue-800' : 'bg-gray-100 dark:bg-gray-800'}`}>
                <DollarSign className={`w-5 h-5 ${formData.optimizationGoal === 'payment' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`} />
              </div>
              <div>
                <div className={`font-medium ${formData.optimizationGoal === 'payment' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'}`}>
                  Снизить ежемесячный платеж
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Уменьшение финансовой нагрузки и увеличение ежемесячного располагаемого дохода
                </div>
              </div>
            </div>
            {formData.optimizationGoal === 'payment' && (
              <div className="absolute top-0 right-0 translate-x-1/4 -translate-y-1/4">
                <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-semibold">Выбрано</span>
              </div>
            )}
          </label>

          {/* Вариант "Сократить срок кредита" */}
          <label className={`
            relative flex items-center p-4 rounded-lg border cursor-pointer transition-colors
            ${formData.optimizationGoal === 'term' 
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-600' 
              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'}
          `}>
            <input
              type="radio"
              name="optimizationGoal"
              value="term"
              checked={formData.optimizationGoal === 'term'}
              onChange={handleInputChange}
              className="form-radio sr-only"
            />
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-full ${formData.optimizationGoal === 'term' ? 'bg-blue-100 dark:bg-blue-800' : 'bg-gray-100 dark:bg-gray-800'}`}>
                <Clock className={`w-5 h-5 ${formData.optimizationGoal === 'term' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`} />
              </div>
              <div>
                <div className={`font-medium ${formData.optimizationGoal === 'term' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'}`}>
                  Сократить срок кредита
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Быстрее закрыть кредит и сэкономить на процентах, сохраняя прежний уровень платежа
                </div>
              </div>
            </div>
            {formData.optimizationGoal === 'term' && (
              <div className="absolute top-0 right-0 translate-x-1/4 -translate-y-1/4">
                <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-semibold">Выбрано</span>
              </div>
            )}
          </label>
        </div>

        {errors.optimizationGoal && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.optimizationGoal}
          </p>
        )}
      </div>

      <div className="mt-4 space-y-3">
        <div className="flex items-center">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="includeEarlyRepayment"
              checked={formData.includeEarlyRepayment}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Планирую делать досрочное погашение
            </span>
          </label>
        </div>
        
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Регулярное досрочное погашение поможет существенно сэкономить на процентах и быстрее закрыть кредит. При расчете будет учтена выбранная стратегия досрочного погашения.
        </p>
      </div>
    </div>
  );
};

export default OptimizationSection;